import React from 'react';
import { Phone, Star, Clock, MapPin, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export default function Hero() {
  const quickLinks = [
    { label: 'Retour Affectif', href: '#service-retour-affectif' },
    { label: 'Voyance', href: '#service-voyance' },
    { label: 'Guérison', href: '#service-guerison' },
    { label: 'Protection', href: '#service-protection' },
    { label: 'Chance', href: '#service-chance' },
  ];

  return (
    <section id="accueil" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay - Fixed dimensions to prevent CLS */}
      <div className="absolute inset-0" style={{ contain: 'layout style paint' }}>
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/91270fd0-1649-4447-95e7-a0e5427e436b/images/22e208562d86f8dcaa1bc08c0580213b63964dca1b3cc94073f8ae56cda3a6c1.png"
          alt="Marabout Marseille - Maître Diamma"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          decoding="sync"
          width="1920"
          height="1080"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-background" />
      </div>

      <div className="container mx-auto px-4 py-8 sm:py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-8">
          {/* Badge */}
          <div className="flex justify-center">
            <Badge variant="outline" className="px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm border-primary/50 bg-primary/10 backdrop-blur-sm">
              <span className="text-primary font-semibold">⭐ 30 ans d'expérience<span className="seo-hidden"> à Marseille</span></span>
            </Badge>
          </div>

          {/* Main Heading */}
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-5xl lg:text-7xl font-bold leading-tight">
              <span className="text-gold-gradient">Maître Diamma</span>
              <br />
              <span className="text-white text-xl sm:text-4xl lg:text-5xl">Grand Marabout Africain<span className="seo-hidden"> Marseille</span></span>
            </h1>
            
            <p className="text-sm sm:text-xl lg:text-2xl font-semibold">
              N°1 du <span className="text-green-500">Retour Affectif</span> Rapide
            </p>
            <p className="text-xs sm:text-lg text-primary font-medium">
              Voyant Médium Sérieux
            </p>
            
            <p className="text-xs sm:text-base lg:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              <span className="text-green-500">Retour de l'être aimé</span> • Voyance • Désenvoûtement
            </p>
          </div>

          {/* Star Rating */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-primary text-sm sm:text-base">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-current" />
              ))}
            </div>
            <span className="text-white font-medium">| 847 avis clients</span>
          </div>

          {/* Quick Links Pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {quickLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-black/40 backdrop-blur-sm border border-primary/30 text-xs sm:text-sm font-medium text-white"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 sm:pt-4">
            <Button 
              asChild 
              size="lg" 
              className="w-full sm:w-auto bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg text-sm sm:text-base"
            >
              <a href="tel:+33745449641" className="flex items-center gap-2">
                <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
                Appeler Maintenant
              </a>
            </Button>
            
            <Button 
              asChild 
              size="lg" 
              className="w-full sm:w-auto bg-green-600 text-white text-sm sm:text-base"
            >
              <a href="https://wa.me/33745449641" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </a>
            </Button>

            <Button 
              asChild 
              size="lg" 
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base"
            >
              <a href="https://paypal.me/PierreMarie888" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.332 0 4.053.64 5.108 1.88.947 1.108 1.283 2.609.997 4.457l-.002.013v.013c-.322 2.077-1.35 3.756-2.99 4.86-1.571 1.056-3.586 1.593-5.99 1.593H7.403a.77.77 0 0 0-.757.63l-.77 4.98a.641.641 0 0 1-.633.54h-.167v-.72zm11.755-14.79c-.006.046-.014.092-.022.138-.633 4.088-3.584 5.5-7.13 5.5H9.832a.872.872 0 0 0-.86.736l-.89 5.65-.253 1.6a.457.457 0 0 0 .451.53h3.165a.764.764 0 0 0 .754-.627l.031-.162.598-3.79.038-.208a.764.764 0 0 1 .754-.627h.474c3.072 0 5.477-1.248 6.179-4.86.293-1.51.141-2.77-.635-3.655a3.03 3.03 0 0 0-.868-.67z"/>
                </svg>
                PayPal
              </a>
            </Button>
          </div>

          {/* Bottom Info Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pt-4 sm:pt-8 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-white/90 bg-black/30 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-white/10">
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
              <span>Disponible<span className="seo-hidden"> Marseille</span></span>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-white/90 bg-black/30 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-white/10">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
              <span>8h à 23h30</span>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-white/90 bg-black/30 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-white/10">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
              <span>07 45 44 96 41</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
